import { useMemo } from 'react';

import type {
  DocumentResultResponse,
  FieldConfidenceResponse,
} from '../api';
import type { FieldEditState } from '../hooks/useFieldEdit';
import { FieldRow } from './FieldRow';
import { GroupFieldRow } from './GroupFieldRow';
import { TableFieldRow } from './TableFieldRow';

interface FieldPanelProps {
  result: DocumentResultResponse;
  edits: Record<string, FieldEditState>;
  highlightedFieldName: string | null;
  onCommit: (fieldName: string, value: unknown) => void;
  onFocusField: (fieldName: string) => void;
}

/**
 * Right column of the reviewer. Renders one row per confidence entry, dispatching to
 * the appropriate component based on field shape:
 *   - Group (has children) → `GroupFieldRow` (accordion with nested FieldRow list).
 *   - Table (value is array of objects) → `TableFieldRow` (accordion wrapping the table).
 *   - Anything else → `FieldRow` (scalar value with EditableValue).
 *
 * The panel itself is unstyled regarding scroll/padding — its parent (the reviewer page
 * column) owns the scroll container so the panel can compose into either the workspace
 * grid or any future layout without redefining its own scroll behaviour.
 */
export function FieldPanel({
  result,
  edits,
  highlightedFieldName,
  onCommit,
  onFocusField,
}: FieldPanelProps): JSX.Element {
  const confidenceFields = useMemo<ReadonlyArray<FieldConfidenceResponse>>(
    () => result.confidence?.fields ?? [],
    [result.confidence?.fields],
  );

  if (confidenceFields.length === 0) {
    return (
      <div className="rounded-2xl border border-slate-200/70 bg-white p-6 text-center text-sm text-slate-500 shadow-sm">
        Sin extracciones disponibles para este documento.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {confidenceFields.map((field, index) => {
        const isGroup = field.children != null && field.children.length > 0;
        const isTable = !isGroup && isTableValue(field.value);
        const valueIsMissing =
          field.value === null || field.value === undefined || field.value === '';
        return (
          <div
            key={field.name}
            className="animate-slide-up-fade-in"
            style={{ animationDelay: `${Math.min(index, 8) * 24}ms` }}
          >
            {isGroup ? (
              <GroupFieldRow
                group={field}
                edits={edits}
                highlightedFieldName={highlightedFieldName}
                onCommit={onCommit}
                onFocus={onFocusField}
              />
            ) : isTable ? (
              <TableFieldRow
                field={field}
                edit={edits[field.name]}
                highlightedFieldName={highlightedFieldName}
              />
            ) : (
              <FieldRow
                field={field}
                edit={edits[field.name]}
                missingRequired={valueIsMissing && field.status === 'critical'}
                highlighted={highlightedFieldName === field.name}
                onCommit={onCommit}
                onFocus={onFocusField}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Loose runtime guard: a table value is an array of plain objects (no schema reference). */
function isTableValue(value: unknown): value is ReadonlyArray<Record<string, unknown>> {
  if (!Array.isArray(value)) {
    return false;
  }
  return value.every(
    (row) => typeof row === 'object' && row !== null && !Array.isArray(row),
  );
}
